import { Article, Page, SiteConfig } from '../types';

const SPACE_ID = process.env.REACT_APP_CONTENTFUL_SPACE_ID || '';
const ACCESS_TOKEN = process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN || '';

export async function fetchContentful(query: string) {
  const url = `https://cdn.contentful.com/spaces/${SPACE_ID}/environments/master/entries?${query}`;

  try {
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${ACCESS_TOKEN}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Contentful API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching from Contentful:', error);
    return null;
  }
}

export async function getSiteConfig(): Promise<SiteConfig | null> {
  const data = await fetchContentful('content_type=siteConfig');

  if (!data || data.items.length === 0) return null;

  const item = data.items[0];

  return {
    siteName: item.fields.siteName || ''
  };
}

export async function getArticles(): Promise<Article[]> {
  const data = await fetchContentful('content_type=article&order=-fields.publishedDate');

  if (!data) return [];

  return data.items.map((item: any) => ({
    id: item.sys.id,
    title: item.fields.title || 'Untitled',
    slug: item.fields.slug || '',
    excerpt: item.fields.excerpt || '',
    content: item.fields.content || '',
    featuredImage: item.fields.featuredImage
      ? `https:${data.includes.Asset.find((a: any) => a.sys.id === item.fields.featuredImage.sys.id)?.fields.file.url}`
      : undefined,
    publishedDate: item.fields.publishedDate || new Date().toISOString(),
    isFeatured: item.fields.isFeatured || false,
    assets: data.includes?.Asset || [],
  }));
}

export async function getArticleBySlug(slug: string): Promise<Article | null> {
  const data = await fetchContentful(`content_type=article&fields.slug=${slug}&limit=1`);

  if (!data || data.items.length === 0) return null;

  const item = data.items[0];
  return {
    id: item.sys.id,
    title: item.fields.title || 'Untitled',
    slug: item.fields.slug || '',
    excerpt: item.fields.excerpt || '',
    content: item.fields.content || '',
    featuredImage: item.fields.featuredImage
      ? `https:${data.includes?.Asset?.find((a: any) => a.sys.id === item.fields.featuredImage.sys.id)?.fields.file.url}`
      : undefined,
    publishedDate: item.fields.publishedDate || new Date().toISOString(),
    isFeatured: item.fields.isFeatured || false,
    assets: data.includes?.Asset || [],
  };
}

export async function getPages(): Promise<Page[]> {
  const data = await fetchContentful('content_type=page&order=fields.order');

  if (!data) return [];

  return data.items.map((item: any) => ({
    id: item.sys.id,
    title: item.fields.title || 'Untitled',
    slug: item.fields.slug || '',
    content: item.fields.content || '',
    assets: data.includes?.Asset || [],
    order: item.fields.order,
    icon: item.fields.icon || 'music',
  }));
}

export async function getPageBySlug(slug: string): Promise<Page | null> {
  const data = await fetchContentful(`content_type=page&fields.slug=${slug}&limit=1`);

  if (!data || data.items.length === 0) return null;

  const item = data.items[0];
  return {
    id: item.sys.id,
    title: item.fields.title || 'Untitled',
    slug: item.fields.slug || '',
    content: item.fields.content || '',
    assets: data.includes?.Asset || [],
    icon: item.fields.icon || 'music',
  };
}

export function isContentfulConfigured(): boolean {
  return !!(SPACE_ID && ACCESS_TOKEN && SPACE_ID !== 'YOUR_SPACE_ID_HERE' && ACCESS_TOKEN !== 'YOUR_ACCESS_TOKEN_HERE');
}